import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Lock, Smartphone, Eye, Bell, KeyRound } from 'lucide-react';
import LegalPageLayout from '../components/LegalPageLayout';

const Security = () => (
  <LegalPageLayout
    title="Security Center"
    subtitle="How we protect your accounts—and how you can stay safe online."
  >
    <p>
      At Aurora Wealth Reserves, safeguarding your financial information is foundational to everything we do.
      This Security Center summarizes the controls we use and the steps you can take to keep your accounts secure.
    </p>

    <h2>Our Protections</h2>
    <div className="not-prose grid sm:grid-cols-2 gap-4 my-6">
      {[
        { icon: Lock, title: 'Encryption', body: 'Industry-standard TLS encryption protects data in transit between your device and our systems.' },
        { icon: KeyRound, title: 'Multi-factor authentication', body: 'One-time codes and verification steps help ensure only you can access online banking.' },
        { icon: Eye, title: 'Fraud monitoring', body: 'We monitor for unusual activity and may contact you or temporarily restrict access when risk is detected.' },
        { icon: Shield, title: 'Access controls', body: 'Internal systems use least-privilege access, logging, and operational safeguards.' },
      ].map(({ icon: Icon, title, body }) => (
        <div key={title} className="rounded-xl border border-slate-200 bg-slate-50 p-4">
          <div className="flex items-center gap-2 text-primary-800 font-semibold mb-2">
            <Icon className="w-5 h-5 text-heritage-600" />
            {title}
          </div>
          <p className="text-sm text-slate-600 m-0">{body}</p>
        </div>
      ))}
    </div>

    <h2>What You Can Do</h2>
    <ul>
      <li>Use a unique, strong password and never share one-time codes</li>
      <li>Enable available multi-factor authentication options</li>
      <li>Keep devices and browsers updated; avoid public Wi‑Fi for banking when possible</li>
      <li>Review account activity regularly and report anything unfamiliar immediately</li>
      <li>Be cautious of phishing emails or messages asking for passwords or codes</li>
    </ul>

    <h2>Phishing &amp; Scams</h2>
    <p>
      Aurora Wealth Reserves will never ask you for your full password or one-time code by email or unsolicited message.
      Always navigate to <strong>aurorawealthreserves.com</strong> directly rather than clicking unexpected links.
      If something looks suspicious, contact us before responding.
    </p>

    <div className="not-prose flex flex-wrap gap-3 my-6">
      <div className="inline-flex items-center gap-2 rounded-lg bg-primary-50 text-primary-800 px-3 py-2 text-sm">
        <Smartphone className="w-4 h-4" /> Secure online banking
      </div>
      <div className="inline-flex items-center gap-2 rounded-lg bg-heritage-50 text-heritage-800 px-3 py-2 text-sm">
        <Bell className="w-4 h-4" /> Report fraud promptly
      </div>
    </div>

    <h2>Report a Concern</h2>
    <p>
      Suspected unauthorized activity:{' '}
      <a href="mailto:support@aurorawealthreserves.com">support@aurorawealthreserves.com</a>.
      You can also reach us via our <Link to="/contact">Contact</Link> page. Review our{' '}
      <Link to="/privacy">Privacy Policy</Link> for how we handle personal data.
    </p>
  </LegalPageLayout>
);

export default Security;
